// ServicesTimelineTab.jsx
// Per-trip "Services Timeline" — a chronological log of service milestones for the
// selected client trip (e.g. "Visa submitted", "Flight ticketed"). Each entry has an
// optional title, a note and a date. Operators can add, edit, view and delete entries.
//
// Backed by the new sub-resource endpoints:
//   list   → GET    /crm/clients/:id/trips/:tripIndex/services-timeline   (also embedded on the client)
//   add    → POST   /crm/clients/:id/trips/:tripIndex/services-timeline
//   edit   → PATCH  /crm/clients/:id/trips/:tripIndex/services-timeline/:entryIndex
//   delete → DELETE /crm/clients/:id/trips/:tripIndex/services-timeline/:entryIndex
//
// Entries live inside the client document, so after every mutation we call onChanged()
// (which reloads the client in the parent modal) and re-render from the fresh prop.
import React, { useState } from "react";
import { api } from "../api";
import { fmtDate } from "../utils";
import { useToast, useConfirm, Btn, Inp, Txa } from "../ui";
import { Ico, EmptyTabState } from "./primitives";
import { G, GL } from "./constants";

const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().split("T")[0];
};

const emptyForm = { title: "", notes: "", date: "" };

// ── Inline add/edit editor ──
function EntryEditor({ initial = emptyForm, saving, onSave, onCancel, submitLabel }) {
  const [f, setF] = useState({
    title: initial.title || "",
    notes: initial.notes || "",
    date: toDateInput(initial.date),
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const canSave = f.notes.trim() || f.title.trim();

  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3 bg-slate-50/60">
      <div className="grid grid-cols-2 gap-3">
        <Inp label="Title" placeholder="e.g. Visa submitted" value={f.title} onChange={set("title")} />
        <Inp label="Date" type="date" value={f.date} onChange={set("date")} />
      </div>
      <Txa label="Notes" placeholder="What happened / details…" value={f.notes} onChange={set("notes")} />
      <div className="flex gap-2 justify-end">
        <Btn variant="secondary" size="sm" onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" size="sm" loading={saving} disabled={!canSave} onClick={() => onSave(f)}>
          {submitLabel}
        </Btn>
      </div>
    </div>
  );
}

export default function ServicesTimelineTab({ client, tripIndex, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();

  const [adding, setAdding] = useState(false);
  const [editIdx, setEditIdx] = useState(null); // original array index being edited
  const [saving, setSaving] = useState(false);

  const trip = client?.trips?.[tripIndex];
  const base = `/crm/clients/${client._id}/trips/${tripIndex}/services-timeline`;

  // Keep original index (backend indexes by position) but display newest-first.
  const entries = (trip?.servicesTimeline || []).map((e, idx) => ({ ...e, _idx: idx }));
  const ordered = [...entries].sort(
    (a, b) => new Date(b.date || 0) - new Date(a.date || 0)
  );

  const doAdd = async (form) => {
    setSaving(true);
    try {
      await api.post(base, form);
      toast("Timeline entry added ✓");
      setAdding(false);
      await onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doEdit = async (idx, form) => {
    setSaving(true);
    try {
      await api.patch(`${base}/${idx}`, form);
      toast("Timeline entry updated ✓");
      setEditIdx(null);
      await onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (idx) => {
    if (!(await confirm({
      title: "Delete entry?",
      message: "This timeline entry will be permanently removed.",
      confirmText: "Delete entry",
    }))) return;
    try {
      await api.delete(`${base}/${idx}`);
      toast("Timeline entry deleted");
      await onChanged?.();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  if (!trip) {
    return (
      <EmptyTabState
        icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        label="No trip selected"
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {entries.length} timeline entr{entries.length !== 1 ? "ies" : "y"} for this trip
        </span>
        {!adding && (
          <button
            onClick={() => { setAdding(true); setEditIdx(null); }}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl text-white"
            style={{ background: G }}
          >
            <Ico d="M12 4v16m8-8H4" sz={11} col="white" /> Add Entry
          </button>
        )}
      </div>

      {adding && (
        <EntryEditor
          saving={saving}
          submitLabel="Add Entry"
          onSave={doAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      {!entries.length && !adding ? (
        <EmptyTabState
          icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          label="No service timeline yet"
        />
      ) : (
        <div className="space-y-0">
          {ordered.map((entry, i) => {
            const isEditing = editIdx === entry._idx;
            return (
              <div key={entry._idx} className="flex gap-3">
                {/* rail */}
                <div className="flex flex-col items-center flex-shrink-0 pt-1.5">
                  <span
                    className="w-3 h-3 rounded-full flex-shrink-0 ring-4"
                    style={{ background: G, boxShadow: `0 0 0 4px ${GL}` }}
                  />
                  {i < ordered.length - 1 && <span className="w-px flex-1 bg-slate-200 my-1" />}
                </div>

                {/* body */}
                <div className="flex-1 min-w-0 pb-5">
                  {isEditing ? (
                    <EntryEditor
                      initial={entry}
                      saving={saving}
                      submitLabel="Save Entry"
                      onSave={(form) => doEdit(entry._idx, form)}
                      onCancel={() => setEditIdx(null)}
                    />
                  ) : (
                    <div className="rounded-2xl border border-slate-100 hover:border-slate-200 transition-all p-4 group">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                            {entry.date ? fmtDate(entry.date) : "No date"}
                          </p>
                          {entry.title && (
                            <p className="text-sm font-bold text-slate-800 mt-0.5">{entry.title}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => { setEditIdx(entry._idx); setAdding(false); }}
                            className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                            title="Edit"
                          >
                            <Ico d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" sz={12} />
                          </button>
                          <button
                            onClick={() => doDelete(entry._idx)}
                            className="w-7 h-7 rounded-lg hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors"
                            title="Delete"
                          >
                            <Ico d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" sz={12} />
                          </button>
                        </div>
                      </div>
                      {entry.notes && (
                        <p className="text-sm text-slate-600 leading-relaxed mt-1.5 whitespace-pre-wrap">
                          {entry.notes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}